import { useClient } from '@/composables/useClient'
import type { User } from '@/services/client/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useAuthStore = defineStore(
  'auth',
  () => {
    // State
    const loggedIn = ref(false)
    const role = ref(false)
    const user = ref<User | null>(null)
    const token = ref<string | null>(null)
    const loading = ref(false)
    const error = ref<string | null>(null)
    const lastChecked = ref<number | null>(null)

    loggedIn.value = token ? true : false

    // Get client instance
    const client = useClient()

    // Actions
    const login = async (username: string, password: string) => {
      console.log(`User ${username} is trying to log in...`)
      loading.value = true
      error.value = null

      try {
        const loggedInUser = await client.login(username, password)

        // Update state
        user.value = loggedInUser
        token.value = client.getToken()
        loggedIn.value = true

        console.log(`User ${username} successfully logged in.`)

        return loggedInUser
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Unknown error occurred'
        throw err
      } finally {
        loading.value = false
      }
    }

    const logout = () => {
      console.log(`User ${user.value?.username} is being logged out...`)

      // Clear client state
      client.logout()

      // Clear store state
      loggedIn.value = false
      user.value = null
      token.value = null
      error.value = null
      lastChecked.value = null

      console.log(`User logged out.`)
    }

    const checkPermissions = async (): Promise<boolean> => {
      console.log('Checking user permissions...')
      loading.value = true
      error.value = null

      try {
        if (!await client.verifySession()) {
          throw new Error(`token is invalid`)
        }
        loggedIn.value = true
        lastChecked.value = Date.now()
        console.log('Permissions verified successfully.')
        return true
      } catch (err) {
        console.error('Permission check failed:', err)
        return false
        // logout() // Force logout if permissions are invalid
      } finally {
        loading.value = false
      }
    }

    // Session auto-check every 10 minutes
    const startSessionCheck = () => {
      setInterval(() => {
        const now = Date.now()
        const tenMinutes = 10 * 60 * 1000

        if (loggedIn.value === true && lastChecked.value === null || ( lastChecked.value && now - lastChecked.value > tenMinutes)) {
          checkPermissions()
        }
      }, 60 * 1000) // Check every minute
    }

    // Getters
    const isAuthenticated = computed(() => loggedIn.value && !!token.value)
    const isAdmin = computed(() => isAuthenticated.value && user.value?.role === 'ADMIN')

    return {
      // State
      loggedIn,
      user,
      token,
      loading,
      error,
      lastChecked,

      // Computed
      isAuthenticated,
      isAdmin,

      // Actions
      login,
      logout,
      checkPermissions,
      startSessionCheck,
    }
  },
  {
    persist: {
      key: 'auth',
      storage: localStorage,
    },
  }
)